import React, { useState } from 'react';
import ItemCardContainer from './ItemCardContainer';
import DatabaseColumn from './DatabaseColumn';
import {
  getPresetConfig,
  getStylingTheme,
  mergeConfigs,
  mergeStyling,
  getDatabaseBrowserPreset,
  getDatabaseColumnPreset,
  PRESET_CONFIGS,
  STYLING_THEMES,
  DATABASE_BROWSER_PRESETS,
  DATABASE_COLUMN_PRESETS
} from './ItemCardConfig';

/**
 * Demo Component for Item Card Library
 * 
 * This component demonstrates how to use the ItemCard library
 * with different configurations and styling options.
 */
const ItemCardDemo = () => {
  // Sample data
  const [items, setItems] = useState([
    {
      id: 1,
      description: 'Albacore Tuna',
      name: 'Albacore Tuna',
      location: 'Kitchen Prep Area',
      vendor: 'Forest Gumps',
      par_level: 12,
      min_order_qty: 6,
      min_order_unit: 'case',
      count_position: 1,
      unit: 'each'
    },
    {
      id: 2,
      description: 'ANGUS RIBEYE',
      name: 'ANGUS RIBEYE',
      location: 'Walk-in Cooler',
      vendor: 'Premium Meats',
      par_level: 8,
      min_order_qty: 2,
      min_order_unit: 'case',
      count_position: 2,
      unit: 'lbs'
    },
    {
      id: 3,
      description: 'Organic Spinach',
      name: 'Organic Spinach',
      location: 'Walk-in Cooler',
      vendor: 'Fresh Greens Co',
      par_level: 24,
      min_order_qty: 12,
      min_order_unit: 'bunch',
      count_position: 3,
      unit: 'bunch'
    },
    {
      id: 4,
      description: 'Craft Beer IPA',
      name: 'Craft Beer IPA',
      location: 'Bar Storage',
      vendor: 'Local Brewery',
      par_level: 48,
      min_order_qty: 24,
      min_order_unit: 'bottle',
      count_position: 4,
      unit: 'bottle'
    }
  ]);

  const [currentPreset, setCurrentPreset] = useState('COUNT_SESSION');
  const [currentTheme, setCurrentTheme] = useState('DEFAULT');
  const [currentBrowserPreset, setCurrentBrowserPreset] = useState('COUNT_SESSION');
  const [currentColumnPreset, setCurrentColumnPreset] = useState('STANDARD');
  const [activeTab, setActiveTab] = useState('cards'); // 'cards' or 'database'

  // Sample inventory data for database browser
  const [inventoryData] = useState([
    {
      id: 101,
      description: 'Premium Olive Oil',
      brand: 'Mediterranean Gold',
      category: 'Oils & Vinegars',
      location: 'Pantry',
      vendor: 'Gourmet Supplies',
      unit: 'bottle',
      pack_size: '500ml',
      manufacturer_code: 'MG-OO-500'
    },
    {
      id: 102,
      description: 'Organic Quinoa',
      brand: 'Nature\'s Best',
      category: 'Grains',
      location: 'Dry Storage',
      vendor: 'Organic Foods Co',
      unit: 'lb',
      pack_size: '2lb bag',
      manufacturer_code: 'NB-Q-2LB'
    },
    {
      id: 103,
      description: 'Fresh Salmon Fillet',
      brand: 'Ocean Fresh',
      category: 'Seafood',
      location: 'Walk-in Cooler',
      vendor: 'Seafood Direct',
      unit: 'lb',
      pack_size: '1lb portion',
      manufacturer_code: 'OF-SF-1LB'
    },
    {
      id: 104,
      description: 'Craft Beer IPA',
      brand: 'Local Brewery',
      category: 'Beverages',
      location: 'Bar Storage',
      vendor: 'Beverage Distributors',
      unit: 'bottle',
      pack_size: '12oz bottle',
      manufacturer_code: 'LB-IPA-12'
    },
    {
      id: 105,
      description: 'Stainless Steel Tongs',
      brand: 'Kitchen Pro',
      category: 'Kitchen Tools',
      location: 'Kitchen Storage',
      vendor: 'Restaurant Supply',
      unit: 'each',
      pack_size: '12 inch',
      manufacturer_code: 'KP-T-12'
    }
  ]);

  // Event handlers
  const handleMove = (location, fromIndex, toIndex) => {
    console.log('Move item:', { location, fromIndex, toIndex });
    
    // Update items array
    setItems(prevItems => {
      const locationItems = prevItems.filter(item => 
        (item.location || 'Unassigned') === location
      );
      const otherItems = prevItems.filter(item => 
        (item.location || 'Unassigned') !== location
      );
      
      const [movedItem] = locationItems.splice(fromIndex, 1);
      locationItems.splice(toIndex, 0, movedItem);
      
      // Update positions
      const updatedLocationItems = locationItems.map((item, index) => ({
        ...item,
        count_position: index + 1
      }));
      
      return [...otherItems, ...updatedLocationItems].sort((a, b) => 
        a.count_position - b.count_position
      );
    });
  };

  const handleRemove = (itemId, location) => {
    console.log('Remove item:', { itemId, location });
    setItems(prevItems => prevItems.filter(item => item.id !== itemId));
  };

  const handleUpdatePar = (itemId, location, parLevel, unit) => {
    console.log('Update PAR:', { itemId, location, parLevel, unit });
    setItems(prevItems => 
      prevItems.map(item => 
        item.id === itemId 
          ? { ...item, par_level: parseFloat(parLevel) || 0, par_unit: unit }
          : item
      )
    );
  };

  const handleUpdateMOQ = (itemId, moq) => {
    console.log('Update MOQ:', { itemId, moq });
    setItems(prevItems => 
      prevItems.map(item => 
        item.id === itemId 
          ? { ...item, min_order_qty: parseFloat(moq) || 0 }
          : item
      )
    );
  };

  const handleItemClick = (item, index, location) => {
    console.log('Item clicked:', { item: item.description, index, location });
  };

  // Database browser event handlers
  const handleAddFromDatabase = (item) => {
    console.log('Add from database:', item.description);

    // Convert database item to count item format
    const newItem = {
      id: Date.now(), // Generate new ID for count item
      description: item.description,
      name: item.description,
      location: item.location,
      vendor: item.vendor,
      brand: item.brand,
      category: item.category,
      par_level: 0,
      min_order_qty: 1,
      min_order_unit: item.unit || 'each',
      count_position: items.length + 1,
      unit: item.unit || 'each',
      inventory_item_id: item.id // Reference to original inventory item
    };

    setItems(prev => [...prev, newItem]);
  };

  const handleDatabaseItemClick = (item) => {
    console.log('Database item clicked:', item.description);
  };

  // Get current configuration and styling
  const presetConfig = getPresetConfig(currentPreset);
  const stylingTheme = getStylingTheme(currentTheme);
  const browserPresetConfig = getDatabaseBrowserPreset(currentBrowserPreset);
  const columnPresetConfig = getDatabaseColumnPreset(currentColumnPreset);

  return (
    <div className="container-fluid py-4">
      {/* Tab Navigation */}
      <div className="row mb-4">
        <div className="col-12">
          <ul className="nav nav-tabs">
            <li className="nav-item">
              <button
                className={`nav-link ${activeTab === 'cards' ? 'active' : ''}`}
                onClick={() => setActiveTab('cards')}
              >
                <i className="feather icon-credit-card me-2"></i>
                Item Cards
              </button>
            </li>
            <li className="nav-item">
              <button
                className={`nav-link ${activeTab === 'database' ? 'active' : ''}`}
                onClick={() => setActiveTab('database')}
              >
                <i className="feather icon-database me-2"></i>
                Database Browser
              </button>
            </li>
          </ul>
        </div>
      </div>

      <div className="row">
        {/* Controls */}
        <div className="col-md-3">
          <div className="card">
            <div className="card-header">
              <h5 className="mb-0">Item Card Demo Controls</h5>
            </div>
            <div className="card-body">
              {/* Item Cards Controls */}
              {activeTab === 'cards' && (
                <>
                  {/* Preset Selection */}
                  <div className="mb-3">
                    <label className="form-label">Configuration Preset:</label>
                    <select
                      className="form-select"
                      value={currentPreset}
                      onChange={(e) => setCurrentPreset(e.target.value)}
                    >
                      {Object.keys(PRESET_CONFIGS).map(preset => (
                        <option key={preset} value={preset}>
                          {preset.replace(/_/g, ' ')}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Theme Selection */}
                  <div className="mb-3">
                    <label className="form-label">Styling Theme:</label>
                    <select
                      className="form-select"
                      value={currentTheme}
                      onChange={(e) => setCurrentTheme(e.target.value)}
                    >
                      {Object.keys(STYLING_THEMES).map(theme => (
                        <option key={theme} value={theme}>
                          {theme.replace(/_/g, ' ')}
                        </option>
                      ))}
                    </select>
                  </div>
                </>
              )}

              {/* Database Browser Controls */}
              {activeTab === 'database' && (
                <>
                  {/* Browser Preset Selection */}
                  <div className="mb-3">
                    <label className="form-label">Browser Preset:</label>
                    <select
                      className="form-select"
                      value={currentBrowserPreset}
                      onChange={(e) => setCurrentBrowserPreset(e.target.value)}
                    >
                      {Object.keys(DATABASE_BROWSER_PRESETS).map(preset => (
                        <option key={preset} value={preset}>
                          {preset.replace(/_/g, ' ')}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Column Preset Selection */}
                  <div className="mb-3">
                    <label className="form-label">Column Preset:</label>
                    <select
                      className="form-select"
                      value={currentColumnPreset}
                      onChange={(e) => setCurrentColumnPreset(e.target.value)}
                    >
                      {Object.keys(DATABASE_COLUMN_PRESETS).map(preset => (
                        <option key={preset} value={preset}>
                          {preset.replace(/_/g, ' ')}
                        </option>
                      ))}
                    </select>
                  </div>
                </>
              )}

              {/* Current Configuration Display */}
              <div className="mb-3">
                <h6>Current Config:</h6>
                <small className="text-muted">
                  <div>PAR: {presetConfig.card.showPAR ? 'Yes' : 'No'}</div>
                  <div>MOQ: {presetConfig.card.showMOQ ? 'Yes' : 'No'}</div>
                  <div>Delete: {presetConfig.card.showDelete ? 'Yes' : 'No'}</div>
                  <div>Drag: {presetConfig.card.enableDrag ? 'Yes' : 'No'}</div>
                  <div>Grouping: {presetConfig.container.groupByLocation ? 'Yes' : 'No'}</div>
                  <div>Sort: {presetConfig.container.sortBy}</div>
                </small>
              </div>

              {/* Add Sample Item */}
              <button 
                className="btn btn-sm btn-outline-primary"
                onClick={() => {
                  const newItem = {
                    id: Date.now(),
                    description: `Sample Item ${items.length + 1}`,
                    name: `Sample Item ${items.length + 1}`,
                    location: 'Sample Location',
                    vendor: 'Sample Vendor',
                    par_level: 10,
                    min_order_qty: 5,
                    min_order_unit: 'each',
                    count_position: items.length + 1,
                    unit: 'each'
                  };
                  setItems(prev => [...prev, newItem]);
                }}
              >
                Add Sample Item
              </button>
            </div>
          </div>
        </div>

        {/* Display Area */}
        <div className="col-md-9">
          {/* Item Cards Display */}
          {activeTab === 'cards' && (
            <div className="card">
              <div className="card-header">
                <h5 className="mb-0">
                  Item Cards - {currentPreset.replace(/_/g, ' ')} / {currentTheme.replace(/_/g, ' ')}
                </h5>
                <small className="text-muted">
                  {items.length} items total
                </small>
              </div>
              <div className="card-body">
                <ItemCardContainer
                  items={items}
                  onMove={handleMove}
                  onRemove={handleRemove}
                  onUpdatePar={handleUpdatePar}
                  onUpdateMOQ={handleUpdateMOQ}
                  onItemClick={handleItemClick}
                  config={presetConfig.container}
                  cardConfig={presetConfig.card}
                  styling={stylingTheme}
                />
              </div>
            </div>
          )}

          {/* Database Browser Display */}
          {activeTab === 'database' && (
            <div className="card">
              <div className="card-header">
                <h5 className="mb-0">
                  Database Browser - {currentBrowserPreset.replace(/_/g, ' ')} / {currentColumnPreset.replace(/_/g, ' ')}
                </h5>
                <small className="text-muted">
                  {inventoryData.length} inventory items available
                </small>
              </div>
              <div className="card-body" style={{ height: '600px' }}>
                <DatabaseColumn
                  inventoryData={inventoryData}
                  currentItems={items}
                  onAddItem={handleAddFromDatabase}
                  onItemClick={handleDatabaseItemClick}
                  config={columnPresetConfig}
                  browserConfig={browserPresetConfig}
                />
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Usage Instructions */}
      <div className="row mt-4">
        <div className="col-12">
          <div className="card">
            <div className="card-header">
              <h5 className="mb-0">Usage Instructions</h5>
            </div>
            <div className="card-body">
              <h6>Basic Usage:</h6>
              <pre className="bg-light p-3 rounded">
{`import ItemCardContainer from './components/cards/ItemCardContainer';
import { getPresetConfig, getStylingTheme } from './components/cards/ItemCardConfig';

const config = getPresetConfig('COUNT_SESSION');
const styling = getStylingTheme('DEFAULT');

<ItemCardContainer
  items={items}
  onMove={handleMove}
  onRemove={handleRemove}
  onUpdatePar={handleUpdatePar}
  onUpdateMOQ={handleUpdateMOQ}
  onItemClick={handleItemClick}
  config={config.container}
  cardConfig={config.card}
  styling={styling}
/>`}
              </pre>

              <h6 className="mt-3">Available Presets:</h6>
              <ul>
                <li><strong>COUNT_SESSION:</strong> Full-featured cards for count sessions</li>
                <li><strong>DISPLAY_ONLY:</strong> Read-only cards for display purposes</li>
                <li><strong>INVENTORY:</strong> Cards for inventory management</li>
                <li><strong>SIMPLE_LIST:</strong> Basic list without grouping</li>
                <li><strong>TEMPLATE_EDITOR:</strong> Cards for template editing</li>
              </ul>

              <h6 className="mt-3">Available Themes:</h6>
              <ul>
                <li><strong>DEFAULT:</strong> Standard styling</li>
                <li><strong>COMPACT:</strong> Smaller cards for dense layouts</li>
                <li><strong>LARGE:</strong> Larger cards for accessibility</li>
                <li><strong>MINIMAL:</strong> Clean, minimal styling</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ItemCardDemo;
